import type { BlogPost } from "./types";

// Image guidance from source doc (photo omitted for now; add to public/blog/ later):
//  *   Accessibility auditor reviewing a website on a laptop with a printed checklist — alt: "Accessibility auditor sitting at a laptop reviewing a website against a printed WCAG checklist, with a screen reader software interface visible on the monitor and handwritten audit notes beside the keyboard"
//  *   Sample VPAT / ACR document showing WCAG 2.1 Level AA evaluation table — alt: "Sample Accessibility Conformance Report (ACR/VPAT 2.5) document showing a WCAG 2.1 Level AA evaluation table with Supports, Partially Supports, and Does Not Support conformance levels for individual success criteria"
const post: BlogPost = {
  slug: "how-to-conduct-accessibility-audit-guide",
  title: "How to Conduct an Accessibility Audit",
  metaTitle: "How to Conduct an Accessibility Audit",
  metaDescription:
    "New to digital accessibility? This plain-English guide explains what a WCAG audit involves, who should do it, and what the report should include.",
  subtitle:
    "What a WCAG audit involves, who should carry it out, and what the report should contain, written for business leaders.",
  date: "2026-07-12",
  photo: "/blog/how-to-conduct-accessibility-audit-guide.svg",
  alt:
    "Accessibility auditor sitting at a laptop reviewing a website against a printed WCAG checklist, with a screen reader software interface visible on the monitor and handwritten audit notes beside the keyboard",
  author: "AccessBharat",
  tag: "Audit",
  excerpt:
    "What a WCAG audit involves, who should carry it out, and what the report should contain, written for business leaders.",
  body: `If you have decided to take digital accessibility seriously, the first job is finding out where you stand. An accessibility audit does that: a systematic check of your digital properties against a recognised standard, with documented findings and a remediation roadmap. This guide covers what the process involves, who should do it, and what you should have at the end.

## What an accessibility audit is

An audit is a structured evaluation of your website, web application or mobile app against WCAG, usually version 2.1 or 2.2 at Level AA. The report records which success criteria pass and fail, how severe each failure is, which users it affects, and how to fix it.

A VPAT (Voluntary Product Accessibility Template), once completed, becomes an ACR (Accessibility Conformance Report). It is the formal document used to communicate conformance, particularly in enterprise procurement and regulated sectors.

## Automated and manual testing

Automated tools such as axe-core, WAVE and Lighthouse scan quickly and catch a good share of failures. But they can reliably detect only roughly 30-40% of WCAG failures. Anything that needs human judgment is out of their reach: whether alt text is actually meaningful, whether the reading order makes sense, whether a form error tells the user what to do.

A proper audit uses both. Automated scanning gives breadth and speed. Manual review by an expert gives depth, and it has to include testing with real screen readers. An audit without manual and screen reader testing is an automated scan with a different name.

## Who should do the audit

In India, the DEPwD publishes an official list of empanelled web accessibility auditors. For STQC's GIGW certification, an IAAP-certified auditor is now mandatory. IAAP certifications include the CPACC (Certified Professional in Accessibility Core Competencies) and the WAS (Web Accessibility Specialist).

Whoever you choose, look for:

- IAAP certification
- experience with your type of product and your sector
- the ability to test with real assistive technology
- experience producing VPATs or ACRs

## After the audit

The report is the start of the work. Use the findings to brief your developers, assign an owner to each fix and plan remediation sprints. Schedule a re-audit once the major fixes are done, and publish an accessibility statement on your site that gives your conformance level, the audit date and a way for users to report problems.
`,
};

export default post;
